import '../../styles/interactives.scss'
import { useCallback, useEffect, useRef, useState } from 'react'
const threshold = [0.75, 0.85]

function getSVG(args) {
  const { from, to, width, height, dot, gap, stroke } = args
  const left = (dot + gap) * (from - 1) + dot / 2
  const right = (dot + gap) * (to - 1) + dot / 2
  const oneThird = width * 0.33
  const twoThird = width * 0.66

  return `
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="${width}"
		height="${height}"
		viewBox="0 0 ${width} ${height}"
	>
		<path
			d="M 0 ${left} C ${twoThird} ${left}, ${oneThird} ${right}, ${width} ${right}"
			fill="none"
			stroke-width="${stroke}"
		/>
	</svg>
	`
}

export default function CareerAlignment() {
  const paragraphs = useRef(null)
  const interactive = useRef(null)

  const [windowWidth, setwindowWidth] = useState(window.innerWidth)
  useEffect(() => {
    const handleResize = () => {
      setwindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleSteps = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        // check that we have a ref, and that we are measuring one way only
        if (!interactive?.current || entry?.boundingClientRect.y < 0) return
        const ratio = entry?.intersectionRatio ?? 0

        // On Mount the observer get called with a 0 value
        if (ratio === 0) return

        const paragraph = Number(entry?.target.getAttribute('data-index') ?? 0)
        if (paragraph === 0) return

        const beforeP1 = paragraph === 1 && ratio < threshold[0]
        const afterP1 = paragraph === 1 && ratio > threshold[1]

        const beforeP2 = paragraph === 2 && ratio < threshold[0]
        const afterP2 = paragraph === 2 && ratio > threshold[1]

        const beforeP3 = paragraph === 3 && ratio < threshold[0]
        const afterP3 = paragraph === 3 && ratio > threshold[1]

        if (beforeP1) {
          interactive.current.removeAttribute('data-step')
        } else if (afterP1 || beforeP2) {
          interactive.current.setAttribute('data-step', 1)
        } else if (afterP2 || beforeP3) {
          interactive.current.setAttribute('data-step', 2)
        } else if (afterP3) {
          interactive.current.setAttribute('data-step', 3)
        }
      })
    },
    [interactive]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleSteps, { threshold })

    if (paragraphs?.current) {
      paragraphs.current.querySelectorAll('li').forEach((paragraph, index) => {
        paragraph.setAttribute('data-index', index + 1)
        observer.observe(paragraph)
      })
    }

    return () => observer.disconnect()
  }, [handleSteps, paragraphs])

  useEffect(() => {
    if (!interactive?.current) return

    const getPropertyAsNumber = (element, property) => {
      return Number(window.getComputedStyle(element).getPropertyValue(property))
    }

    const pathsList = interactive.current.querySelector('.paths')
    const paths = pathsList.querySelectorAll('li')

    const dot = getPropertyAsNumber(interactive?.current, '--dot')
    const gap = getPropertyAsNumber(interactive?.current, '--gap')
    const stroke = getPropertyAsNumber(interactive?.current, '--stroke')
    const width = getPropertyAsNumber(pathsList, '--width')
    const height = dot * paths.length + gap * (paths.length - 1)

    const args = { height, stroke, dot, gap, width }

    paths.forEach((path) => {
      switch (path.className) {
        case 'path-Relationships-Finance':
          args.from = 5
          args.to = 1
          break
        case 'path-Status-HR':
          args.from = 6
          args.to = 3
          break
        case 'path-Income-Healthcare':
          args.from = 3
          args.to = 6
          break
        case 'path-Income-DataScience':
          args.from = 3
          args.to = 2
          break
        case 'path-Income-Legal':
          args.from = 3
          args.to = 4
          break
        case 'path-Income-IT':
        default:
          args.from = 3
          args.to = 5
          break
      }

      path.innerHTML = getSVG(args)
    })
  }, [interactive, windowWidth])

  return (
    <section className="interactive" name="Career Alignment">
      <header>
        <h2>
          Similarly, low exposure contributes to{' '}
          <strong> patterns of misalignment</strong> between what motivates
          students and the industries they are likely to&nbsp;pursue
        </h2>
      </header>
      <main>
        <aside>
          <figure ref={interactive} className="career-alignment">
            <ol title="Drivers">
              <li>Creativity</li>
              <li>Expertise</li>
              <li>Income</li>
              <li>Meaning</li>
              <li>Relationships</li>
              <li>Status</li>
            </ol>
            <ul className="paths">
              <li className="path-Relationships-Finance" />
              <li className="path-Status-HR" />
              <li className="path-Income-Healthcare" />
              <li className="path-Income-DataScience" />
              <li className="path-Income-Legal" />
              <li className="path-Income-IT" />
            </ul>
            <ol title="Fields">
              <li>Finance</li>
              <li>Data Science</li>
              <li>HR</li>
              <li>Legal</li>
              <li>IT</li>
              <li>Healthcare</li>
            </ol>
          </figure>
        </aside>
        <ul ref={paragraphs}>
          <li>
            <p>
              Students motivated by <strong>relationships</strong> often pursue
              careers in <strong>Finance</strong> and those motivated by{' '}
              <strong>status</strong> are more likely to pursue{' '}
              <strong>HR</strong>
            </p>
          </li>
          <li>
            <p>
              Students who are most motivated by <strong>salary</strong> often
              pursue <strong>Healthcare</strong>. Some of them will go on to
              become great doctors
            </p>
          </li>
          <li>
            <p>
              Yet, with wider exposure to a broader range of high-paying
              careers, some would discover more alignment between their{' '}
              <strong>motivations & strengths</strong> and{' '}
              <strong>the field they choose</strong>
            </p>
          </li>
        </ul>
      </main>
    </section>
  )
}
